import { useCallback, useRef, useState, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import UserReviewTile from './UserReviewTile'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import RatingDistribution from 'components/Common/RatingDistribution'
import RatingBreakdownItem from 'components/Common/RatingBreakdownItem'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import UserReviewPagination from './UserReviewPagination'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { ReviewCompanionshipType } from 'api/review'
import {
  mapRatingDistribution,
  mapRatingBreakdown,
  getRecommendPercentageText,
} from 'lib/reviews/ratingBreakdownUtils'
import ReviewsPanel from './ReviewsPanel'
import ReviewFilterBar from './ReviewFilterBar'
import type { ReviewPanelSortType } from 'constants/review'
import {
  RATING_RANGE_TO_FILTER_MAP,
} from 'constants/review'

const GraphSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${rem(20)};
`

const RatingBreakdownSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${rem(24)};
  column-gap: ${rem(32)};
  padding: ${rem(16)} ${rem(64)};
`

const RatingBreakdownSectionMobile = styled(Group)`
  padding: 0 ${rem(8)};
`

const MobileGraphSection = styled(VerticalSpacer)`
  padding: ${rem(24)} 0;
`

const SearchResultInfo = styled.div`
  padding: ${rem(12)} 0;
`

const EmptyState = styled.div`
  padding: ${rem(40)} 0;
  text-align: center;
`

const PAGE_SIZE = 4

interface Props {
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  showReviewFiltersAndUserDetails?: boolean
  reviewsThreshold?: number
}

const DEFAULT_SORT: ReviewPanelSortType = 'recommend'

function UserReviewSection({
  property,
  isLPP,
  experience,
  tour,
  showReviewFiltersAndUserDetails = true,
  reviewsThreshold,
}: Props) {
  const intl = useIntl()
  const rootRef = useRef<HTMLDivElement>(null)
  // As the user changes pages, we want to show the old reviews until it's done loading
  // so we'll store them here when this happens so we can display them
  const [isReviewsPanelOpen, setIsReviewsPanelOpen] = useState(false)
  const [initialRatingFilter, setInitialRatingFilter] = useState<string>('')
  const [initialReviewId, setInitialReviewId] = useState<string>('')
  const [localPage, setLocalPage] = useState<number>(1)
  const [oldReviews, setOldReviews] = useState<Array<App.Review>>([])
  const [searchInput, setSearchInput] = useState<string>('')
  const [activeSearch, setActiveSearch] = useState<string>('')

  // Filter state
  const [sort, setSort] = useState<ReviewPanelSortType>(DEFAULT_SORT)
  const [ratingFilter, setRatingFilter] = useState<string>('')
  const [travellerFilter, setTravellerFilter] = useState<ReviewCompanionshipType | ''>('')

  const onOpenReviewsPanel = useCallback(() => {
    setInitialRatingFilter('')
    setInitialReviewId('')
    setIsReviewsPanelOpen(true)
  }, [])

  const onOpenReviewsPanelWithReview = useCallback((reviewId: string) => {
    setInitialRatingFilter('')
    setInitialReviewId(reviewId)
    setIsReviewsPanelOpen(true)
  }, [])

  const onOpenReviewsPanelWithFilter = useCallback((ratingRange: string) => {
    const filter = RATING_RANGE_TO_FILTER_MAP[ratingRange] || ''
    setInitialRatingFilter(filter)
    setIsReviewsPanelOpen(true)
  }, [])

  const onCloseReviewsPanel = useCallback(() => {
    setIsReviewsPanelOpen(false)
    setInitialRatingFilter('')
    setInitialReviewId('')
  }, [])

  // Search handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }, [])

  const handleSearchSubmit = useCallback(() => {
    setActiveSearch(searchInput)
    setLocalPage(1)
    setOldReviews([])
  }, [searchInput])

  // Sort handler — reset pagination inline to avoid extra render from useEffect
  const handleSortChange = useCallback((value: ReviewPanelSortType) => {
    setSort(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  // Rating filter handler
  const handleRatingChange = useCallback((value: string) => {
    setRatingFilter(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  // Traveller filter handler
  const handleTravellerChange = useCallback((value: ReviewCompanionshipType | '') => {
    setTravellerFilter(value)
    setLocalPage(1)
    setOldReviews([])
  }, [])

  const offset = (localPage - 1) * PAGE_SIZE
  const [reviews, fetchingReviews] = useUserReviews({
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    limit: PAGE_SIZE,
    offset,
    sort,
    ratings: ratingFilter ? [ratingFilter] : undefined,
    companionship: travellerFilter ? [travellerFilter] : undefined,
    search: activeSearch || undefined,
  })

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'

  const reviewsFromHook = reviews?.reviews
  const visibleReviews = useMemo(
    () => fetchingReviews ? oldReviews : reviewsFromHook ?? [],
    [fetchingReviews, oldReviews, reviewsFromHook],
  )
  const visibleReviewsRef = useRef(visibleReviews)
  visibleReviewsRef.current = visibleReviews

  const enrichedReviews = useMemo(() => visibleReviews.map(review => ({
    ...review,
    experienceId: experience?.id,
    tourId: tour?.id,
  })), [visibleReviews, experience?.id, tour?.id])

  const onPageChange = useCallback((page: number) => {
    setLocalPage(page)
    setOldReviews(visibleReviewsRef.current)
    rootRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating, reviewsThreshold)

  // Determine if "View all reviews" CTA should be shown
  // Hide for Google reviews as they cannot be viewed in our platform
  const displayReviewsNumber = offerRatingDisplayable && !isGoogleReviews

  // Extract rating breakdown data from property, experience, or tour
  // Note: Currently only properties have reviewsRatingBreakdown in the backend
  const ratingBreakdown =
    property?.rating?.reviewsRatingBreakdown ??
    experience?.rating?.reviewsRatingBreakdown ??
    tour?.rating?.reviewsRatingBreakdown

  // Map rating distribution from backend format to frontend format
  const ratingDistributions = useMemo(
    () => mapRatingDistribution(ratingBreakdown?.ratingDistribution, intl),
    [ratingBreakdown?.ratingDistribution, intl],
  )

  // Map rating breakdown items from backend format to frontend format
  const ratingBreakdowns = useMemo(
    () => mapRatingBreakdown(ratingBreakdown, intl),
    [ratingBreakdown, intl],
  )

  // Get recommend percentage text
  const entityType = property ? 'hotel' : tour ? 'tour' : 'experience'
  const recommendText = useMemo(
    () => getRecommendPercentageText(ratingBreakdown?.recommendPercentage, entityType, intl),
    [ratingBreakdown?.recommendPercentage, entityType, intl],
  )

  // Translated strings
  const reviewsFromGoogleSubtitle = intl.formatMessage({
    id: 'BwsW6IPCoTz2oPVsFO7-',
    defaultMessage: 'Reviews from Google',
    description: 'Subtitle shown when reviews are sourced from Google',
  })

  const verifiedGuestsHeading = intl.formatMessage({
    id: 'xUq2Aop57PZud70LjEZG',
    defaultMessage: 'Reviews from verified guests',
    description: 'Heading for the customer reviews section in the reviews section',
  })

  const viewAllCountLabel = intl.formatMessage(
    {
      id: 'aBsHJY_YhJF8dXewFI0j',
      defaultMessage: 'View all {count, plural, one {# review} other {# reviews}}',
      description: 'Button text to open reviews panel showing total review count',
    },
    { count: displayableRating?.reviewsTotal },
  )
  const viewAllLabel = intl.formatMessage({
    id: '5v7CEGr2rIr7Fl5IHqLX',
    defaultMessage: 'View all reviews',
    description: 'Button text to open reviews panel without showing count',
  })
  const viewAllReviewsLabel = displayReviewsNumber ? viewAllCountLabel : viewAllLabel

  const noFilterResultsText = intl.formatMessage({
    id: '4tG4kfAhcxdLsPUUaBb9',
    defaultMessage: 'No reviews found with the selected filters',
    description: 'Empty state message when review filters return no results',
  })

  const reviewsPaginationLabel = intl.formatMessage({
    id: 'IWseQkGj7w4-3kjm1EES',
    defaultMessage: 'Reviews',
    description: 'Accessible label for the reviews pagination section',
  })

  return (
    <Group
      direction="vertical"
      gap={24}
      ref={rootRef}
    >
      <LoadingIndicator inline visible={fetchingReviews} />
      {/* Graph Section - Desktop */}
      {!isGoogleReviews && <><CSSBreakpoint min="tablet">
        <GraphSection>
          {/* Review Section */}
          <VerticalSpacer gap={32}>
            {offerRatingDisplayable && <NumberRating
              variant="xxl"
              rating={displayableRating.score}
              total={displayableRating.reviewsTotal}
              ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
              onReviewSubTitleClick={onOpenReviewsPanel}
            />}
            {recommendText && (
              <BodyTextBlock
                weight="bold"
                variant="medium"
                startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
              >
                {recommendText}
              </BodyTextBlock>
            )}
          </VerticalSpacer>
          {/* Rating Distribution */}
          {ratingDistributions.length > 0 && (
            <VerticalSpacer gap={0}>
              {ratingDistributions.map(item => (
                <RatingDistribution
                  key={item.id}
                  label={item.label}
                  range={item.range}
                  progress={item.progress}
                  count={item.count}
                  onClick={() => onOpenReviewsPanelWithFilter(item.range)}
                />
              ))}
            </VerticalSpacer>
          )}
          {/* Rating Breakdown */}
          {ratingBreakdowns.length > 0 && (
            <RatingBreakdownSection>
              {ratingBreakdowns.map(item => (
                <RatingBreakdownItem
                  key={item.id}
                  score={item.score}
                  label={item.label}
                />
              ))}
            </RatingBreakdownSection>
          )}
        </GraphSection>
      </CSSBreakpoint>
      {/* Graph Section - Mobile */}
      <CSSBreakpoint max="mobile">
        <MobileGraphSection gap={24}>
          {/* Review Section */}
          {offerRatingDisplayable && <NumberRating
            variant="xl"
            rating={displayableRating.score}
            total={displayableRating.reviewsTotal}
            ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
            onReviewSubTitleClick={onOpenReviewsPanel}
          />}
          {/* Rating Distribution */}
          {ratingDistributions.length > 0 && (
            <VerticalSpacer gap={8}>
              {ratingDistributions.map(item => (
                <RatingDistribution
                  key={item.id}
                  label={item.label}
                  range={item.range}
                  progress={item.progress}
                  count={item.count}
                  onClick={() => onOpenReviewsPanelWithFilter(item.range)}
                />
              ))}
            </VerticalSpacer>
          )}
          {/* Rating Breakdown */}
          {ratingBreakdowns.length > 0 && (
            <RatingBreakdownSectionMobile direction="horizontal" gap={0} horizontalAlign="space-between">
              {ratingBreakdowns.map(item => (
                <RatingBreakdownItem
                  key={item.id}
                  score={item.score}
                  label={item.label}
                />
              ))}
            </RatingBreakdownSectionMobile>
          )}
          {recommendText && (
            <BodyTextBlock
              weight="bold"
              variant="medium"
              startIcon={<LineThumbsUpIcon colour="highlight-primary-dark" />}
            >
              {recommendText}
            </BodyTextBlock>
          )}
        </MobileGraphSection>
      </CSSBreakpoint>
      </>}
      {/* Reviews Header - Desktop */}
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          {offerRatingDisplayable && isGoogleReviews && <NumberRating
            variant="xxl"
            rating={displayableRating.score}
            total={displayableRating.reviewsTotal}
            ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
            onReviewSubTitleClick={onOpenReviewsPanel}
          />}
          {!isGoogleReviews && <Heading variant="heading4">{verifiedGuestsHeading}</Heading>}
          <TextButton kind="secondary" onClick={onOpenReviewsPanel}>{viewAllReviewsLabel}</TextButton>
        </Group>
      </CSSBreakpoint>
      {/* Reviews Header - Tablet */}
      <CSSBreakpoint max="tablet">
        {isGoogleReviews && offerRatingDisplayable && <NumberRating
          variant="xl"
          rating={displayableRating.score}
          total={displayableRating.reviewsTotal}
          ratingSubtitle={isGoogleReviews ? reviewsFromGoogleSubtitle : undefined}
          onReviewSubTitleClick={onOpenReviewsPanel}
        />}
        {!isGoogleReviews && <Heading variant="heading4">{verifiedGuestsHeading}</Heading>}
      </CSSBreakpoint>
      {/* Search Input & Filter Chips */}
      {!isGoogleReviews && (
        <ReviewFilterBar
          searchInput={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          sort={sort}
          defaultSort={DEFAULT_SORT}
          onSortChange={handleSortChange}
          ratingFilter={ratingFilter}
          onRatingChange={handleRatingChange}
          travellerFilter={travellerFilter}
          onTravellerChange={handleTravellerChange}
        />
      )}
      {/* Search Result Info */}
      {activeSearch && visibleReviews.length > 0 && (
        <SearchResultInfo>
          <BodyText variant="medium">
            <FormattedMessage
              id="UOvFDp3obMhIEEEW-0Qt"
              defaultMessage="{count, plural, one {# review} other {# reviews}} mentioned ''<b>{searchTerm}</b>''"
              description="Text showing how many reviews mention the user search term"
              values={{
                count: reviews?.total ?? visibleReviews.length,
                searchTerm: activeSearch,
                b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
        </SearchResultInfo>
      )}
      {activeSearch && visibleReviews.length === 0 && !fetchingReviews && (
        <EmptyState>
          <BodyText variant="medium">
            <FormattedMessage
              id="1-ox8un5C1nlh0o7af1a"
              defaultMessage="There are no results for ''<b>{searchTerm}</b>''"
              description="Empty state message when review search returns no results"
              values={{
                searchTerm: activeSearch,
                b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
        </EmptyState>
      )}
      {!activeSearch && (ratingFilter || travellerFilter) && visibleReviews.length === 0 && !fetchingReviews && (
        <EmptyState>
          <BodyText variant="medium">
            {noFilterResultsText}
          </BodyText>
        </EmptyState>
      )}
      {/* Reviews List with Pagination */}
      {!!visibleReviews.length && <UserReviewPagination
        label={reviewsPaginationLabel}
        total={reviews?.total ?? 0}
        pageSize={PAGE_SIZE}
        currentPage={localPage}
        onPageChange={onPageChange}
      >
        <Group direction="vertical" gap={24}>
          {enrichedReviews.map(review => <UserReviewTile
            key={review.id}
            userReview={review}
            showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
            onOpenReviewsPanel={onOpenReviewsPanelWithReview}
            searchTerm={activeSearch}
          />)}
        </Group>
      </UserReviewPagination>}
      {/* View All - Tablet */}
      <CSSBreakpoint max="tablet">
        <Group direction="horizontal" horizontalAlign="center">
          <TextButton kind="secondary" onClick={onOpenReviewsPanel}>{viewAllReviewsLabel}</TextButton>
        </Group>
      </CSSBreakpoint>

      <ReviewsPanel
        isOpen={isReviewsPanelOpen}
        onClose={onCloseReviewsPanel}
        property={property}
        isLPP={isLPP}
        experience={experience}
        tour={tour}
        initialRatingFilter={initialRatingFilter}
        initialReviewId={initialReviewId}
        initialSort={DEFAULT_SORT}
      />
    </Group>
  )
}

export default React.memo(UserReviewSection)
